import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, TrendingDown, Minus, BarChart3, Eye, FileText, DollarSign, Target, ArrowRightLeft, Building2 } from 'lucide-react';

const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 rounded-xl";
const selectContentCls = "bg-[#0A1F1A] border-white/10 text-white";

function TrendArrow({ current, comparison, invert = false }) {
  if (comparison === 0 && current === 0) return <Minus size={16} className="text-white/30" />;
  const diff = current - comparison;
  const better = invert ? diff < 0 : diff > 0;
  const same = diff === 0;
  if (same) return <Minus size={16} className="text-white/30" />;
  return better
    ? <TrendingUp size={16} className="text-[#6BCB77]" />
    : <TrendingDown size={16} className="text-red-400" />;
}

function MetricCard({ label, icon: Icon, current, comparison, format = 'number', color = 'text-white', invert = false }) {
  const fmt = (v) => {
    if (format === 'cop') return formatCOP(v);
    if (format === 'pct') return `${v}%`;
    return v?.toLocaleString('es-CO') || '0';
  };
  return (
    <div className="bg-[#16302B] rounded-xl p-4 border border-white/5" data-testid={`report-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-white/50 uppercase tracking-wider">{label}</span>
        <Icon size={16} strokeWidth={1.5} className="text-[#A38560]" />
      </div>
      <div className="flex items-end justify-between">
        <p className={`font-outfit text-xl font-bold ${color} tabular-nums`}>{fmt(current)}</p>
        <div className="flex items-center gap-1.5">
          <TrendArrow current={current} comparison={comparison} invert={invert} />
          <span className="text-xs text-white/40 tabular-nums">{fmt(comparison)}</span>
        </div>
      </div>
    </div>
  );
}

export default function Report() {
  const { user } = useAuth();
  const [brokers, setBrokers] = useState([]);
  const [brokerId, setBrokerId] = useState('all');
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [compare, setCompare] = useState('last1');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/users').then(setBrokers).catch(() => {}); }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/admin/report?broker_id=${brokerId}&month=${month}&compare=${compare}`);
      setData(d);
    } catch { /* report load error */ }
    setLoading(false);
  }, [brokerId, month, compare]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const c = data?.current || {};
  const p = data?.comparison || {};
  const ytd = data?.ytd || {};

  const compareLabel = {
    'last1': 'mes anterior',
    'last3': 'promedio ultimos 3 meses',
    'last6': 'promedio ultimos 6 meses',
    'year': 'promedio ano calendario',
  }[compare] || compare;

  const brokerName = brokerId === 'all' ? 'Consolidado' : brokers.find(b => b.id === brokerId)?.name || '';

  return (
    <div data-testid="report-page" className="space-y-6">
      <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Reporte</h1>

      {/* Selectors */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-white/50 uppercase tracking-wider mb-1.5 block">Broker</label>
            <Select value={brokerId} onValueChange={setBrokerId}>
              <SelectTrigger className={inputCls} data-testid="report-broker-select">
                <SelectValue placeholder="Seleccionar broker" />
              </SelectTrigger>
              <SelectContent className={selectContentCls}>
                <SelectItem value="all">Consolidado (todos)</SelectItem>
                {brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-white/50 uppercase tracking-wider mb-1.5 block">Mes del reporte</label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="report-month-input"
              className="w-full bg-[#0A1F1A] border border-[#1A3B32] text-white h-11 rounded-xl px-3 focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] outline-none" />
          </div>
          <div>
            <label className="text-xs text-white/50 uppercase tracking-wider mb-1.5 block">Comparar con</label>
            <Select value={compare} onValueChange={setCompare}>
              <SelectTrigger className={inputCls} data-testid="report-compare-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={selectContentCls}>
                <SelectItem value="last1">Mes anterior</SelectItem>
                <SelectItem value="last3">Ultimos 3 meses</SelectItem>
                <SelectItem value="last6">Ultimos 6 meses</SelectItem>
                <SelectItem value="year">Ano calendario</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading && <p className="text-white/40 text-sm">Cargando reporte...</p>}

      {data && !loading && (
        <>
          {/* Header */}
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-outfit text-lg font-medium text-white flex items-center gap-2">
                <BarChart3 size={18} className="text-[#A38560]" />
                {brokerName} — {month}
              </h2>
              <Badge className="bg-white/5 text-white/50 border-0 text-xs">vs {compareLabel}</Badge>
            </div>
            {data.comp_months?.length > 0 && (
              <p className="text-xs text-white/30">Meses de comparacion: {data.comp_months.join(', ')}</p>
            )}
          </div>

          {/* Main Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <MetricCard label="Visitas" icon={Eye} current={c.visits} comparison={p.visits} />
            <MetricCard label="Ofertas" icon={FileText} current={c.offers} comparison={p.offers} color="text-[#A38560]" />
            <MetricCard label="Cierres" icon={DollarSign} current={c.closings} comparison={p.closings} color="text-[#6BCB77]" />
            <MetricCard label="Valor Ventas" icon={Building2} current={c.sales_value} comparison={p.sales_value} format="cop" />
            <MetricCard label="Valor Arriendos" icon={Building2} current={c.rental_value} comparison={p.rental_value} format="cop" />
            <MetricCard label="Comisiones Generadas" icon={Target} current={c.commissions} comparison={p.commissions} format="cop" color="text-[#6BCB77]" />
          </div>

          {/* Conversion Funnel */}
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
            <h3 className="font-outfit text-base font-medium text-white mb-4 flex items-center gap-2">
              <ArrowRightLeft size={16} className="text-[#A38560]" /> Embudo de Conversion
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Visitas a Ofertas</p>
                <p className="font-outfit text-2xl font-bold text-white">{c.v2o}%</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendArrow current={c.v2o} comparison={p.v2o} />
                  <span className="text-xs text-white/40">vs {p.v2o}%</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Ofertas a Cierres</p>
                <p className="font-outfit text-2xl font-bold text-[#A38560]">{c.o2c}%</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendArrow current={c.o2c} comparison={p.o2c} />
                  <span className="text-xs text-white/40">vs {p.o2c}%</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Visitas a Cierres</p>
                <p className="font-outfit text-2xl font-bold text-[#6BCB77]">{c.v2c}%</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <TrendArrow current={c.v2c} comparison={p.v2c} />
                  <span className="text-xs text-white/40">vs {p.v2c}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Income Goal */}
          {data.income_goal > 0 && (
            <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
              <h3 className="font-outfit text-base font-medium text-white mb-3 flex items-center gap-2">
                <Target size={16} className="text-[#6BCB77]" /> Meta de Ingreso Promedio Mensual
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/50">Meta mensual</p>
                  <p className="font-outfit text-xl font-bold text-white">{formatCOP(data.income_goal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/50">Comisiones del mes</p>
                  <p className={`font-outfit text-xl font-bold ${c.commissions >= data.income_goal ? 'text-[#6BCB77]' : 'text-[#A38560]'}`}>
                    {formatCOP(c.commissions)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/50">Cumplimiento</p>
                  <p className={`font-outfit text-xl font-bold ${c.commissions >= data.income_goal ? 'text-[#6BCB77]' : 'text-red-400'}`}>
                    {data.income_goal > 0 ? `${Math.round(c.commissions / data.income_goal * 100)}%` : '-'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* YTD Totals */}
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
            <h3 className="font-outfit text-base font-medium text-white mb-4 flex items-center gap-2">
              <BarChart3 size={16} className="text-[#A38560]" /> Acumulado Ano {month.split('-')[0]}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#16302B] rounded-xl p-4 text-center border border-white/5">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Ventas</p>
                <p className="font-outfit text-lg font-bold text-white tabular-nums">{formatCOP(ytd.sales)}</p>
              </div>
              <div className="bg-[#16302B] rounded-xl p-4 text-center border border-white/5">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Arriendos</p>
                <p className="font-outfit text-lg font-bold text-[#A38560] tabular-nums">{formatCOP(ytd.rentals)}</p>
              </div>
              <div className="bg-[#16302B] rounded-xl p-4 text-center border border-white/5">
                <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Comisiones Generadas</p>
                <p className="font-outfit text-lg font-bold text-[#6BCB77] tabular-nums">{formatCOP(ytd.commissions)}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
